import { useState, useEffect } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { RsvpQuestion } from '../../api/client';

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuestionAnswer {
  questionId: string;
  answer: string;
}

interface CustomQuestionsFormProps {
  questions: RsvpQuestion[];
  onChange: (answers: QuestionAnswer[]) => void;
  initialAnswers?: QuestionAnswer[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function CustomQuestionsForm({
  questions,
  onChange,
  initialAnswers,
}: CustomQuestionsFormProps) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    if (initialAnswers) {
      for (const a of initialAnswers) {
        initial[a.questionId] = a.answer;
      }
    }
    return initial;
  });

  // Notify parent when answers change
  useEffect(() => {
    const answerArray: QuestionAnswer[] = Object.entries(answers)
      .filter(([, value]) => value.trim().length > 0)
      .map(([questionId, answer]) => ({ questionId, answer }));
    onChange(answerArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  const updateAnswer = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  // Handle multiselect checkbox toggle
  const toggleMultiselectOption = (questionId: string, option: string) => {
    setAnswers((prev) => {
      const current = prev[questionId] ?? '';
      const selectedOptions = current ? current.split(',').map((s) => s.trim()) : [];
      const index = selectedOptions.indexOf(option);

      if (index >= 0) {
        selectedOptions.splice(index, 1);
      } else {
        selectedOptions.push(option);
      }

      return { ...prev, [questionId]: selectedOptions.join(', ') };
    });
  };

  const isMultiselectChecked = (questionId: string, option: string): boolean => {
    const current = answers[questionId] ?? '';
    const selectedOptions = current ? current.split(',').map((s) => s.trim()) : [];
    return selectedOptions.includes(option);
  };

  if (questions.length === 0) {
    return null;
  }

  const sortedQuestions = [...questions].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Stack spacing={2.5}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
        Additional Questions
      </Typography>

      {sortedQuestions.map((q) => {
        const options = Array.isArray(q.options) ? (q.options as string[]) : [];

        switch (q.type) {
          case 'select':
            return (
              <FormControl key={q.id} fullWidth size="small" required={q.required}>
                <InputLabel>{q.question}</InputLabel>
                <Select
                  value={answers[q.id] ?? ''}
                  label={q.question}
                  onChange={(e) => updateAnswer(q.id, e.target.value)}
                >
                  {!q.required && (
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                  )}
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                {q.required && (
                  <FormHelperText>Required</FormHelperText>
                )}
              </FormControl>
            );

          case 'multiselect':
            return (
              <FormControl
                key={q.id}
                component="fieldset"
                required={q.required}
                sx={{ width: '100%' }}
              >
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  {q.question}
                  {q.required && ' *'}
                </FormLabel>
                <FormGroup>
                  {options.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={isMultiselectChecked(q.id, option)}
                          onChange={() => toggleMultiselectOption(q.id, option)}
                          size="small"
                        />
                      }
                      label={option}
                    />
                  ))}
                </FormGroup>
                {q.required && !answers[q.id] && (
                  <FormHelperText>Please select at least one option</FormHelperText>
                )}
              </FormControl>
            );

          case 'text':
          default:
            return (
              <TextField
                key={q.id}
                label={q.question}
                value={answers[q.id] ?? ''}
                onChange={(e) => updateAnswer(q.id, e.target.value)}
                required={q.required}
                multiline
                rows={2}
                fullWidth
                size="small"
                inputProps={{ maxLength: 2000 }}
              />
            );
        }
      })}
    </Stack>
  );
}
